import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../api/axios';
import './TeeTimeAlertsPage.css';
import logo from '../assets/teesergolf-logo.png';
import Navigation from '../components/Navigation';

function TeeTimeAlertsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [coursesByState, setCoursesByState] = useState({});
  const [expandedStates, setExpandedStates] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [saveError, setSaveError] = useState('');

  // Configuration state
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [playerCount, setPlayerCount] = useState(1);
  const [dateSelectionMode, setDateSelectionMode] = useState('specific'); // 'specific' or 'recurring'
  const [selectedDates, setSelectedDates] = useState([]);
  const [selectedDaysOfWeek, setSelectedDaysOfWeek] = useState([]);
  const [useCustomTimes, setUseCustomTimes] = useState(false);
  const [defaultTimeRange, setDefaultTimeRange] = useState({
    start: 7,
    startMinute: 0,
    end: 12,
    endMinute: 0
  }); // 7:00 AM to 12:00 PM
  const [customTimeRangesByDate, setCustomTimeRangesByDate] = useState({});
  const [customTimeRangesByDayOfWeek, setCustomTimeRangesByDayOfWeek] = useState({});
  const [showConfiguration, setShowConfiguration] = useState(false);

  // Available hours (6 AM to 6 PM)
  const MIN_HOUR = 6;
  const MAX_HOUR = 18;
  const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const playerCounts = [1, 2, 3, 4];

  // Generate time options in 15-minute increments
  const generateTimeOptions = () => {
    const options = [];
    for (let hour = MIN_HOUR; hour <= MAX_HOUR; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        if (hour === MAX_HOUR && minute > 0) break; // Stop at 6:00 PM
        options.push({ hour, minute });
      }
    }
    return options;
  };

  const timeOptions = generateTimeOptions();

  // Format time for display (e.g., "7:00 AM", "12:30 PM")
  const formatTime = (hour, minute = 0) => {
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    const displayMinute = minute.toString().padStart(2, '0');
    return `${displayHour}:${displayMinute} ${period}`;
  };

  // Convert time string back to hour and minute
  const parseTimeString = (timeStr) => {
    const match = timeStr.match(/(\d+):(\d+)\s*(AM|PM)/i);
    if (!match) return { hour: 7, minute: 0 };

    let hour = parseInt(match[1]);
    const minute = parseInt(match[2]);
    const period = match[3].toUpperCase();

    if (period === 'PM' && hour !== 12) hour += 12;
    if (period === 'AM' && hour === 12) hour = 0;

    return { hour, minute };
  };

  // Convert hour and minute to time string for select value
  const timeToString = (hour, minute = 0) => {
    return formatTime(hour, minute);
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await axiosInstance.get('/courses');
      const coursesData = response.data;
      setCourses(coursesData);

      // Organize courses by state code
      const organized = coursesData.reduce((acc, course) => {
        const stateCode = course.stateCode || 'Unknown';
        if (!acc[stateCode]) {
          acc[stateCode] = [];
        }
        acc[stateCode].push(course);
        return acc;
      }, {});

      setCoursesByState(organized);
    } catch (err) {
      console.error('Error fetching courses:', err);
      setError('Failed to load courses. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleState = (state) => {
    setExpandedStates(prev => ({
      ...prev,
      [state]: !prev[state]
    }));
  };

  const toggleCourseSelection = (course) => {
    setSelectedCourses(prev => {
      const isSelected = prev.some(c => c.courseId === course.courseId);
      if (isSelected) {
        return prev.filter(c => c.courseId !== course.courseId);
      } else {
        return [...prev, course];
      }
    });
  };

  const isCourseSelected = (courseId) => {
    return selectedCourses.some(c => c.courseId === courseId);
  };

  const getAvailableDates = () => {
    const dates = [];
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 15); // 14 days from tomorrow

    const current = new Date(tomorrow);
    while (current <= maxDate) {
      // Format date in local timezone, not UTC
      const year = current.getFullYear();
      const month = String(current.getMonth() + 1).padStart(2, '0');
      const day = String(current.getDate()).padStart(2, '0');
      dates.push(`${year}-${month}-${day}`);
      current.setDate(current.getDate() + 1);
    }

    return dates;
  };

  const toggleDateSelection = (date) => {
    setSelectedDates(prev => {
      if (prev.includes(date)) {
        return prev.filter(d => d !== date);
      } else {
        return [...prev, date].sort();
      }
    });
  };

  const toggleDayOfWeekSelection = (dayIndex) => {
    setSelectedDaysOfWeek(prev => {
      if (prev.includes(dayIndex)) {
        return prev.filter(d => d !== dayIndex);
      } else {
        return [...prev, dayIndex].sort((a, b) => a - b);
      }
    });
  };

  const handleDefaultStartTimeChange = (timeStr) => {
    const { hour, minute } = parseTimeString(timeStr);
    setDefaultTimeRange(prev => {
      const newStartTotal = hour * 60 + minute;
      const currentEndTotal = prev.end * 60;

      // Auto-correct: if new start is after current end, move end forward
      if (newStartTotal >= currentEndTotal) {
        // Set end to 1 hour after start
        const newEndHour = Math.min(hour + 1, MAX_HOUR);
        return { start: hour, startMinute: minute, end: newEndHour, endMinute: 0 };
      }

      return { ...prev, start: hour, startMinute: minute };
    });
  };

  const handleDefaultEndTimeChange = (timeStr) => {
    const { hour, minute } = parseTimeString(timeStr);
    setDefaultTimeRange(prev => {
      const newEndTotal = hour * 60 + minute;
      const currentStartTotal = (prev.start || 7) * 60 + (prev.startMinute || 0);

      // Auto-correct: if new end is before current start, move start backward
      if (newEndTotal <= currentStartTotal) {
        // Set start to 1 hour before end
        const newStartHour = Math.max(hour - 1, MIN_HOUR);
        return { start: newStartHour, startMinute: 0, end: hour, endMinute: minute };
      }

      return { ...prev, end: hour, endMinute: minute };
    });
  };

  const handleCustomStartTimeForDate = (date, timeStr) => {
    const { hour, minute } = parseTimeString(timeStr);
    setCustomTimeRangesByDate(prev => {
      const current = prev[date] || defaultTimeRange;
      const newStartTotal = hour * 60 + minute;
      const currentEndTotal = current.end * 60 + (current.endMinute || 0);

      // Auto-correct: if new start is after current end, move end forward
      if (newStartTotal >= currentEndTotal) {
        const newEndHour = Math.min(hour + 1, MAX_HOUR);
        return {
          ...prev,
          [date]: { start: hour, startMinute: minute, end: newEndHour, endMinute: 0 }
        };
      }

      return {
        ...prev,
        [date]: { ...current, start: hour, startMinute: minute }
      };
    });
  };

  const handleCustomEndTimeForDate = (date, timeStr) => {
    const { hour, minute } = parseTimeString(timeStr);
    setCustomTimeRangesByDate(prev => {
      const current = prev[date] || defaultTimeRange;
      const newEndTotal = hour * 60 + minute;
      const currentStartTotal = current.start * 60 + (current.startMinute || 0);

      // Auto-correct: if new end is before current start, move start backward
      if (newEndTotal <= currentStartTotal) {
        const newStartHour = Math.max(hour - 1, MIN_HOUR);
        return {
          ...prev,
          [date]: { start: newStartHour, startMinute: 0, end: hour, endMinute: minute }
        };
      }

      return {
        ...prev,
        [date]: { ...current, end: hour, endMinute: minute }
      };
    });
  };

  const handleCustomStartTimeForDayOfWeek = (dayIndex, timeStr) => {
    const { hour, minute } = parseTimeString(timeStr);
    setCustomTimeRangesByDayOfWeek(prev => {
      const current = prev[dayIndex] || defaultTimeRange;
      const newStartTotal = hour * 60 + minute;
      const currentEndTotal = current.end * 60 + (current.endMinute || 0);

      // Auto-correct: if new start is after current end, move end forward
      if (newStartTotal >= currentEndTotal) {
        const newEndHour = Math.min(hour + 1, MAX_HOUR);
        return {
          ...prev,
          [dayIndex]: { start: hour, startMinute: minute, end: newEndHour, endMinute: 0 }
        };
      }

      return {
        ...prev,
        [dayIndex]: { ...current, start: hour, startMinute: minute }
      };
    });
  };

  const handleCustomEndTimeForDayOfWeek = (dayIndex, timeStr) => {
    const { hour, minute } = parseTimeString(timeStr);
    setCustomTimeRangesByDayOfWeek(prev => {
      const current = prev[dayIndex] || defaultTimeRange;
      const newEndTotal = hour * 60 + minute;
      const currentStartTotal = current.start * 60 + (current.startMinute || 0);

      // Auto-correct: if new end is before current start, move start backward
      if (newEndTotal <= currentStartTotal) {
        const newStartHour = Math.max(hour - 1, MIN_HOUR);
        return {
          ...prev,
          [dayIndex]: { start: newStartHour, startMinute: 0, end: hour, endMinute: minute }
        };
      }

      return {
        ...prev,
        [dayIndex]: { ...current, end: hour, endMinute: minute }
      };
    });
  };

  const applyDefaultToAllDates = () => {
    if (dateSelectionMode === 'specific') {
      const newCustomRanges = {};
      selectedDates.forEach(date => {
        newCustomRanges[date] = { ...defaultTimeRange };
      });
      setCustomTimeRangesByDate(newCustomRanges);
    } else {
      const newCustomRanges = {};
      selectedDaysOfWeek.forEach(dayIndex => {
        newCustomRanges[dayIndex] = { ...defaultTimeRange };
      });
      setCustomTimeRangesByDayOfWeek(newCustomRanges);
    }
  };

  const handleContinueToConfiguration = () => {
    if (selectedCourses.length === 0) {
      alert('Please select at least one course');
      return;
    }
    setShowConfiguration(true);
  };

  const handleBackToCourses = () => {
    setShowConfiguration(false);
  };

  const handleSubmitConfiguration = async () => {
    // Validate configuration
    if (selectedCourses.length === 0) {
      setSaveError('Please select at least one course');
      return;
    }

    if (dateSelectionMode === 'specific' && selectedDates.length === 0) {
      setSaveError('Please select at least one date');
      return;
    }

    if (dateSelectionMode === 'recurring' && selectedDaysOfWeek.length === 0) {
      setSaveError('Please select at least one day of the week');
      return;
    }

    setIsSaving(true);
    setSaveError('');

    try {
      const alertsToCreate = [];

      // Create alert objects for each course
      selectedCourses.forEach(course => {
        if (dateSelectionMode === 'specific') {
          // Create an alert for each specific date
          selectedDates.forEach(date => {
            const range = useCustomTimes
              ? (customTimeRangesByDate[date] || defaultTimeRange)
              : defaultTimeRange;

            alertsToCreate.push({
              courseName: course.name,
              courseId: course.courseId,
              date: date,
              dateSelectionMode: 'specific',
              recurringDays: null,
              timeOfDayMinHour: range.start,
              timeOfDayMaxHour: range.end,
              playerCount: playerCount,
              isActive: true
            });
          });
        } else {
          // Create a single recurring alert
          const recurringDaysToUse = selectedDaysOfWeek;

          alertsToCreate.push({
            courseName: course.name,
            courseId: course.courseId,
            date: null,
            dateSelectionMode: 'recurring',
            recurringDays: recurringDaysToUse,
            timeOfDayMinHour: defaultTimeRange.start,
            timeOfDayMaxHour: defaultTimeRange.end,
            playerCount: playerCount,
            isActive: true
          });
        }
      });

      // Create all alerts
      await Promise.all(
        alertsToCreate.map(alert => axiosInstance.post('/alerts', alert))
      );

      // Show success message and redirect to dashboard
      alert(`${alertsToCreate.length} alert(s) created successfully!`);
      navigate('/dashboard');
    } catch (error) {
      console.error('Error creating alerts:', error);

      if (error.response?.data?.message) {
        setSaveError(error.response.data.message);
      } else if (error.response?.data?.errors) {
        setSaveError(error.response.data.errors.join(', '));
      } else {
        setSaveError('Failed to create alerts. Please try again.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    // Parse date string as local date, not UTC
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const getDayOfWeek = (dateStr) => {
    // Parse date string as local date, not UTC
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed
    return date.getDay();
  };

  if (showConfiguration) {
    const availableDates = getAvailableDates();
    const activeDates = dateSelectionMode === 'specific' ? selectedDates : selectedDaysOfWeek;

    return (
      <div className="tee-time-alerts-page">
        <header className="alerts-header">
          <div className="header-content">
            <div className="header-logo">
              <img src={logo} alt="TeeserGolf" />
            </div>
            <Navigation />
          </div>
        </header>

        <main className="alerts-main">
          <div className="alerts-content">
            <button className="back-button" onClick={handleBackToCourses}>
              ← Back to Course Selection
            </button>

            <h1>Configure Alert Times</h1>
            <p className="alerts-subtitle">
              Set your preferred tee times for {selectedCourses.length} selected course{selectedCourses.length !== 1 ? 's' : ''}
            </p>

            <div className="configuration-container">
              {/* Player Count Section */}
              <div className="player-count-section">
                <h2>Number of Players</h2>
                <p className="section-subtitle">How many players do you need tee times for?</p>
                <div className="player-count-grid">
                  {playerCounts.map(count => (
                    <button
                      key={count}
                      className={`player-count-button ${playerCount === count ? 'selected' : ''}`}
                      onClick={() => setPlayerCount(count)}
                    >
                      {count} {count === 1 ? 'Player' : 'Players'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Date Selection Section */}
              <div className="date-range-section">
                <h2>Select Dates</h2>

                <div className="date-mode-selector">
                  <button
                    className={`mode-button ${dateSelectionMode === 'specific' ? 'active' : ''}`}
                    onClick={() => setDateSelectionMode('specific')}
                  >
                    Specific Dates
                  </button>
                  <button
                    className={`mode-button ${dateSelectionMode === 'recurring' ? 'active' : ''}`}
                    onClick={() => setDateSelectionMode('recurring')}
                  >
                    Recurring Days
                  </button>
                </div>

                {dateSelectionMode === 'specific' ? (
                  <div className="dates-selection">
                    <p className="section-subtitle">Select specific dates within the next 2 weeks</p>
                    <div className="dates-grid">
                      {availableDates.map(date => (
                        <div
                          key={date}
                          className={`date-card ${selectedDates.includes(date) ? 'selected' : ''}`}
                          onClick={() => toggleDateSelection(date)}
                        >
                          <input
                            type="checkbox"
                            checked={selectedDates.includes(date)}
                            onChange={() => {}}
                          />
                          <span className="date-label">{formatDate(date)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="days-selection">
                    <p className="section-subtitle">Select days of the week for ongoing alerts</p>
                    <div className="days-grid">
                      {daysOfWeek.map((day, index) => (
                        <div
                          key={index}
                          className={`day-card ${selectedDaysOfWeek.includes(index) ? 'selected' : ''}`}
                          onClick={() => toggleDayOfWeekSelection(index)}
                        >
                          <input
                            type="checkbox"
                            checked={selectedDaysOfWeek.includes(index)}
                            onChange={() => {}}
                          />
                          <span className="day-label">{day}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Time Configuration Section */}
              <div className="time-configuration-section">
                <div className="time-mode-selector">
                  <button
                    className={`mode-button ${!useCustomTimes ? 'active' : ''}`}
                    onClick={() => setUseCustomTimes(false)}
                  >
                    Same Hours for All {dateSelectionMode === 'specific' ? 'Dates' : 'Days'}
                  </button>
                  <button
                    className={`mode-button ${useCustomTimes ? 'active' : ''}`}
                    onClick={() => setUseCustomTimes(true)}
                  >
                    Customize Each {dateSelectionMode === 'specific' ? 'Date' : 'Day'}
                  </button>
                </div>

                {!useCustomTimes ? (
                  <div className="default-hours-section">
                    <h2>Select Time Range</h2>
                    <p className="section-subtitle">Choose the time range you want to be alerted for tee times</p>
                    <div className="time-picker-container">
                      <div className="time-picker-group">
                        <label htmlFor="earliest-time">Earliest tee time</label>
                        <select
                          id="earliest-time"
                          className="time-select"
                          value={timeToString(defaultTimeRange.start, defaultTimeRange.startMinute || 0)}
                          onChange={(e) => handleDefaultStartTimeChange(e.target.value)}
                        >
                          {timeOptions.map(({ hour, minute }) => (
                            <option key={`${hour}-${minute}`} value={timeToString(hour, minute)}>
                              {formatTime(hour, minute)}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="time-picker-group">
                        <label htmlFor="latest-time">Latest tee time</label>
                        <select
                          id="latest-time"
                          className="time-select"
                          value={timeToString(defaultTimeRange.end, defaultTimeRange.endMinute || 0)}
                          onChange={(e) => handleDefaultEndTimeChange(e.target.value)}
                        >
                          {timeOptions.map(({ hour, minute }) => (
                            <option key={`${hour}-${minute}`} value={timeToString(hour, minute)}>
                              {formatTime(hour, minute)}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="time-range-summary">
                      We'll alert you for tee times between {formatTime(defaultTimeRange.start, defaultTimeRange.startMinute || 0)} and {formatTime(defaultTimeRange.end, defaultTimeRange.endMinute || 0)}
                    </div>
                  </div>
                ) : (
                  <div className="custom-hours-section">
                    <div className="custom-hours-header">
                      <h2>Customize Time Range by {dateSelectionMode === 'specific' ? 'Date' : 'Day'}</h2>
                      <button className="apply-default-button" onClick={applyDefaultToAllDates}>
                        Apply Default Time Range to All {dateSelectionMode === 'specific' ? 'Dates' : 'Days'}
                      </button>
                    </div>

                    <div className="default-hours-mini">
                      <h3>Default Time Range Template</h3>
                      <div className="time-picker-container">
                        <div className="time-picker-group">
                          <label htmlFor="template-earliest-time">Earliest tee time</label>
                          <select
                            id="template-earliest-time"
                            className="time-select"
                            value={timeToString(defaultTimeRange.start, defaultTimeRange.startMinute || 0)}
                            onChange={(e) => handleDefaultStartTimeChange(e.target.value)}
                          >
                            {timeOptions.map(({ hour, minute }) => (
                              <option key={`${hour}-${minute}`} value={timeToString(hour, minute)}>
                                {formatTime(hour, minute)}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="time-picker-group">
                          <label htmlFor="template-latest-time">Latest tee time</label>
                          <select
                            id="template-latest-time"
                            className="time-select"
                            value={timeToString(defaultTimeRange.end, defaultTimeRange.endMinute || 0)}
                            onChange={(e) => handleDefaultEndTimeChange(e.target.value)}
                          >
                            {timeOptions.map(({ hour, minute }) => (
                              <option key={`${hour}-${minute}`} value={timeToString(hour, minute)}>
                                {formatTime(hour, minute)}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="dates-list">
                      {dateSelectionMode === 'specific' ? (
                        selectedDates.map(date => {
                          const range = customTimeRangesByDate[date] || defaultTimeRange;
                          return (
                            <div key={date} className="date-hours-section">
                              <h3>{formatDate(date)}</h3>
                              <div className="time-picker-container">
                                <div className="time-picker-group">
                                  <label htmlFor={`earliest-${date}`}>Earliest tee time</label>
                                  <select
                                    id={`earliest-${date}`}
                                    className="time-select"
                                    value={timeToString(range.start, range.startMinute || 0)}
                                    onChange={(e) => handleCustomStartTimeForDate(date, e.target.value)}
                                  >
                                    {timeOptions.map(({ hour, minute }) => (
                                      <option key={`${hour}-${minute}`} value={timeToString(hour, minute)}>
                                        {formatTime(hour, minute)}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                <div className="time-picker-group">
                                  <label htmlFor={`latest-${date}`}>Latest tee time</label>
                                  <select
                                    id={`latest-${date}`}
                                    className="time-select"
                                    value={timeToString(range.end, range.endMinute || 0)}
                                    onChange={(e) => handleCustomEndTimeForDate(date, e.target.value)}
                                  >
                                    {timeOptions.map(({ hour, minute }) => (
                                      <option key={`${hour}-${minute}`} value={timeToString(hour, minute)}>
                                        {formatTime(hour, minute)}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                              <div className="time-range-summary">
                                {formatTime(range.start, range.startMinute || 0)} – {formatTime(range.end, range.endMinute || 0)}
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        selectedDaysOfWeek.map(dayIndex => {
                          const range = customTimeRangesByDayOfWeek[dayIndex] || defaultTimeRange;
                          return (
                            <div key={dayIndex} className="date-hours-section">
                              <h3>Every {daysOfWeek[dayIndex]}</h3>
                              <div className="time-picker-container">
                                <div className="time-picker-group">
                                  <label htmlFor={`earliest-day-${dayIndex}`}>Earliest tee time</label>
                                  <select
                                    id={`earliest-day-${dayIndex}`}
                                    className="time-select"
                                    value={timeToString(range.start, range.startMinute || 0)}
                                    onChange={(e) => handleCustomStartTimeForDayOfWeek(dayIndex, e.target.value)}
                                  >
                                    {timeOptions.map(({ hour, minute }) => (
                                      <option key={`${hour}-${minute}`} value={timeToString(hour, minute)}>
                                        {formatTime(hour, minute)}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                <div className="time-picker-group">
                                  <label htmlFor={`latest-day-${dayIndex}`}>Latest tee time</label>
                                  <select
                                    id={`latest-day-${dayIndex}`}
                                    className="time-select"
                                    value={timeToString(range.end, range.endMinute || 0)}
                                    onChange={(e) => handleCustomEndTimeForDayOfWeek(dayIndex, e.target.value)}
                                  >
                                    {timeOptions.map(({ hour, minute }) => (
                                      <option key={`${hour}-${minute}`} value={timeToString(hour, minute)}>
                                        {formatTime(hour, minute)}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                              <div className="time-range-summary">
                                {formatTime(range.start, range.startMinute || 0)} – {formatTime(range.end, range.endMinute || 0)}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>

              {saveError && (
                <div className="error-message">
                  {saveError}
                </div>
              )}

              <div className="configuration-actions">
                <button
                  className="submit-config-button"
                  onClick={handleSubmitConfiguration}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save Alert Configuration'}
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="tee-time-alerts-page">
      <header className="alerts-header">
        <div className="header-content">
          <div className="header-logo">
            <img src={logo} alt="TeeserGolf" />
          </div>
          <Navigation />
        </div>
      </header>

      <main className="alerts-main">
        <div className="alerts-content">
          <h1>Configure Tee Time Alerts</h1>
          <p className="alerts-subtitle">
            Select courses to receive alerts when tee times become available
          </p>

          {selectedCourses.length > 0 && (
            <div className="selection-summary">
              <div className="summary-content">
                <span className="selected-count">
                  {selectedCourses.length} course{selectedCourses.length !== 1 ? 's' : ''} selected
                </span>
                <button className="continue-button" onClick={handleContinueToConfiguration}>
                  Continue to Time Configuration →
                </button>
              </div>
            </div>
          )}

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {isLoading ? (
            <div className="loading-message">
              Loading courses...
            </div>
          ) : (
            <div className="courses-container">
              {Object.keys(coursesByState).sort().map(state => (
                <div key={state} className="state-section">
                  <div
                    className="state-header"
                    onClick={() => toggleState(state)}
                  >
                    <span className="state-toggle">
                      {expandedStates[state] ? '▼' : '▶'}
                    </span>
                    <h2>{state}</h2>
                    <span className="course-count">
                      ({coursesByState[state].length} courses)
                    </span>
                  </div>

                  {expandedStates[state] && (
                    <div className="courses-list">
                      {coursesByState[state].map(course => (
                        <div
                          key={course.courseId}
                          className={`course-item ${isCourseSelected(course.courseId) ? 'selected' : ''}`}
                          onClick={() => toggleCourseSelection(course)}
                        >
                          <div className="course-checkbox" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={isCourseSelected(course.courseId)}
                              onChange={() => toggleCourseSelection(course)}
                            />
                          </div>
                          <div className="course-info">
                            <h3>{course.name}</h3>
                            <p className="course-location">
                              {course.stateCode}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {Object.keys(coursesByState).length === 0 && !isLoading && (
                <div className="no-courses-message">
                  No courses available at this time.
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default TeeTimeAlertsPage;
